import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import { sendBack } from "@/services/appointment-go-back.service";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { calendarDay } from "@/lib/appointment-reschedule";
import { isGoBackDay } from "@/lib/appointment-go-back";

interface MarkAsGobackModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
}

export default function MarkAsGobackModal({
  isOpen,
  onClose,
  appointment,
  onSuccess,
}: MarkAsGobackModalProps) {
  const [gobackDate, setGobackDate] = useState<Date | undefined>(undefined);
  const [gobackReasons, setGobackReasons] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Set default date to tomorrow if no date is set
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      setGobackDate(tomorrow);
      setGobackReasons("");
    } else {
      setGobackDate(undefined);
      setGobackReasons("");
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!gobackDate) {
      toast.error("Please select a go back date");
      return;
    }

    setLoading(true);
    try {
      const updatedAppointment = await sendBack(appointment.id, calendarDay(gobackDate), gobackReasons);

      toast.success("Appointment marked as go back");
      if (onSuccess) {
        onSuccess(updatedAppointment);
      }
      onClose();
    } catch (error) {
      console.error("Failed to mark appointment as go back:", error);
      toast.error(fieldActionMessage(error, "Failed to mark appointment as go back"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark as Go Back</DialogTitle>
          <DialogDescription>
            Select a date for the go back and provide reasons. This will update the appointment status to "go back".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Go Back Date */}
          <div className="space-y-2">
            <Label>Go Back Date</Label>
            <Calendar
              mode="single"
              selected={gobackDate}
              onSelect={setGobackDate}
              disabled={(date) => !isGoBackDay(date)}
              className="rounded-md border"
            />
          </div>

          {/* Go Back Reasons */}
          <div className="space-y-2">
            <Label htmlFor="goback-reasons">Go Back Reasons</Label>
            <Textarea
              id="goback-reasons"
              placeholder="Enter reasons for go back (optional)..."
              value={gobackReasons}
              onChange={(e) => setGobackReasons(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!gobackDate || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              "Mark as Go Back"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
